import { useLocalStorage, useMediaQuery } from '@mantine/hooks'
import { ThemeProvider } from '@mui/material/styles'
import Cookies from 'js-cookie'
import type { FC, ReactNode } from 'react'
import { createContext, useContext, useEffect, useMemo } from 'react'
import { darkTheme, lightTheme } from 'src/theme'
import { trackerSetUserProperties } from 'src/utils/tracker'
import { COLOR_MODE_COOKIE_KEY, type ColorModeValue } from './constant'

type Mode = ColorModeValue | 'auto'

let colorModeValue: ColorModeValue
export const getColorModeValue = () => colorModeValue

const ColorModeContext = createContext<{
  mode?: Mode
  themeType: ColorModeValue
  changeColorMode: (mode: Mode) => void
}>({ mode: 'light', themeType: 'light', changeColorMode: () => {} })

export const useColorModeContext = () => useContext(ColorModeContext)

function setColorModeToCookie(colorMode: ColorModeValue) {
  return Cookies.set(COLOR_MODE_COOKIE_KEY, colorMode, {
    domain: window.location.hostname.endsWith('pixai.art')
      ? '.pixai.art'
      : undefined,
    expires: 365,
    path: '/',
    sameSite: 'Lax',
  })
}

const ColorModeProvider: FC<{
  children: ReactNode
  initialMode?: ColorModeValue
}> = ({ children, initialMode }) => {
  const [mode, setMode] = useLocalStorage<Mode>({
    key: 'app-color-theme',
    defaultValue: initialMode ?? 'light',
  })

  const isPreferDark = useMediaQuery('(prefers-color-scheme: dark)')
  const isPreferLight = useMediaQuery('(prefers-color-scheme: light)')

  const themeType = (colorModeValue = useMemo<ColorModeValue>(() => {
    return mode === 'auto' ? (isPreferDark ? 'dark' : 'light') : mode
  }, [mode, isPreferDark]))

  const theme = useMemo(() => {
    return themeType === 'dark' ? darkTheme : lightTheme
  }, [themeType])

  useEffect(() => {
    if (themeType === 'dark') {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }, [themeType])

  useEffect(() => {
    let scheme: 'dark' | 'light' | 'no-preference' = 'no-preference'
    if (isPreferDark) {
      scheme = 'dark'
    } else if (isPreferLight) {
      scheme = 'light'
    }
    trackerSetUserProperties({
      systemColorPreference: scheme,
    })
  }, [isPreferDark, isPreferLight])

  useEffect(() => {
    if (typeof document === 'undefined') return

    setColorModeToCookie(themeType)
  }, [themeType])

  const value = useMemo(() => {
    return {
      mode,
      themeType,
      changeColorMode: setMode,
    }
  }, [mode, setMode, themeType])

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ColorModeContext.Provider>
  )
}

export default ColorModeProvider
